import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert as Alert2 } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { api, helpers } from "../utils";
// Imported directly (not via the "../components" barrel) so this doesn't depend on export
// ordering inside index.js — index.js itself re-exports MultiWorkoutBuilder, which transitively
// imports this file, creating a circular import through the barrel that happens to resolve today
// only because FilterSelect's export line comes earlier in index.js than MultiWorkoutBuilder's.
import FilterSelect from "./FilterSelect";
import WorkoutSection from "./WorkoutSection";
import useAutosave from "../hooks/useAutosave";
import { sanitizeProgramForSave } from "./WorkoutColumn.helpers";
import { MdClose } from "react-icons/md";

const STATUS_CHIP = {
  saving: { label: "Saving…", className: "saving" },
  saved: { label: "✓ Saved", className: "saved" },
  error: { label: "Error", className: "error" },
};

// Compact react-select styling so the Phase/Period/Workout dropdowns fit this screen's narrow,
// fixed-width columns (mockup: ~7 columns visible side by side with horizontal scroll).
const COMPACT_SELECT_STYLES = {
  control: (provided) => ({
    ...provided,
    minHeight: "30px",
    fontSize: "0.78rem",
    boxShadow: "none",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 6px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  option: (provided) => ({
    ...provided,
    fontSize: "0.78rem",
    padding: "4px 8px",
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: "0.78rem",
  }),
  // Menu is portaled to <body> (see menuPortalTarget on each FilterSelect) so it isn't clipped by
  // the section card's `overflow: hidden` or the horizontal-scroll columns container; keep it above
  // everything.
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
};

// Portal the dropdown menus to <body> so they escape the clipping overflow contexts above them.
const MENU_PORTAL_TARGET = typeof document !== "undefined" ? document.body : null;

// One fully self-contained Workout Builder column. Everything editing/loading/saving-related for
// THIS workout lives in local state here — the page (`MultiWorkoutBuilder`) only ever passes this
// component read-only props (shared option lists, the shared movementId->MovementCategoryId map,
// the shared duplicated-category set, the highlight toggle, and a stable `onReport` callback used
// purely to publish a lightweight summary of this column back up for cross-column duplicate/
// conflict detection). No sibling column's state is ever read or written from here.
export default function WorkoutColumn({
  columnId,
  orgId,
  activeWorkoutsOnly,
  phaseList,
  periodList,
  workoutList,
  mvmtList,
  duplicatedMovements,
  highlightDuplicates,
  hasSelectionConflict,
  canRemove,
  onRemove,
  onReport,
}) {
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // In "Active only" mode the Phase/Period/Workout chain is cascading (mirrors ProgramMovementMatrix):
  // the shared `periodList`/`workoutList` props arrive empty, and THIS column fetches its own
  // filtered Period list on Phase change and filtered Workout list on Period change — so two columns
  // with different Phases each get their own valid downstream options. When the toggle is off the
  // shared props already hold the full lists and no on-demand fetch runs.
  const [activePeriodList, setActivePeriodList] = useState([]);
  const [activeWorkoutList, setActiveWorkoutList] = useState([]);

  const effectivePeriodList = activeWorkoutsOnly ? activePeriodList : periodList || [];
  const effectiveWorkoutList = activeWorkoutsOnly ? activeWorkoutList : workoutList || [];

  // Cascading fetches — ported from ProgramMovementMatrix.jsx's GetCascadingPeriods/GetCascadingWorkouts.
  const loadFilteredPeriods = useCallback(
    (phaseId) => {
      if (!orgId || !phaseId) {
        setActivePeriodList([]);
        return;
      }
      api
        .fetch(`WorkoutAdmin/GetFilteredPeriods/${orgId}/${phaseId}`)
        .then((r) => {
          if (r.data && r.data.success) {
            setActivePeriodList(helpers.idNameToValueLabel(r.data.message || []));
          }
        })
        .catch(api.catchHandler);
    },
    [orgId]
  );

  const loadFilteredWorkouts = useCallback(
    (phaseId, periodId) => {
      if (!orgId || !phaseId || !periodId) {
        setActiveWorkoutList([]);
        return;
      }
      api
        .fetch(`WorkoutAdmin/GetFilteredWorkouts/${orgId}/${phaseId}/${periodId}`)
        .then((r) => {
          if (r.data && r.data.success) {
            setActiveWorkoutList(helpers.idNameToValueLabel(r.data.message || []));
          }
        })
        .catch(api.catchHandler);
    },
    [orgId]
  );

  const saveProgram = useCallback((snapshot) => {
    return api.post("WorkoutAdmin/SaveProgram", snapshot);
    // Deliberately no `.then` re-fetch here — see useAutosave's own comment. Re-fetching after a
    // successful save would disturb this column's own edit state for no benefit, and is exactly
    // the pattern this screen intentionally departs from (contrast WorkoutBook.jsx/
    // ProgramMovementMatrix.jsx, which both re-GET after saving).
  }, []);

  // Autosave is fed a SANITIZED snapshot, never the raw editable `program` — see "Autosave
  // suppression" in the plan. `saveSnapshot` strips movementId:0 rows and now-empty categories on
  // every render; `saveKey` is its content fingerprint; `stableSaveSnapshot` only changes identity
  // when that fingerprint actually changes. This means appending a blank block/row (which the
  // sanitizer prunes right back out) does not re-arm useAutosave's debounce, so blank edits never
  // POST — a save only fires once a real movement is chosen.
  const saveSnapshot = useMemo(
    () => (program ? sanitizeProgramForSave(program) : null),
    [program]
  );
  const saveKey = useMemo(
    () => (saveSnapshot ? JSON.stringify(saveSnapshot) : null),
    [saveSnapshot]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableSaveSnapshot = useMemo(() => saveSnapshot, [saveKey]);

  const { status: saveStatus, reset: resetAutosave } = useAutosave(stableSaveSnapshot, saveProgram, {
    enabled: !loading,
  });

  // Load this column's own program whenever ITS OWN Phase/Period/Workout selection is complete.
  // Reset autosave's baseline first so the freshly loaded snapshot isn't mistaken for an edit.
  useEffect(() => {
    if (!orgId || !selectedPhase || !selectedPeriod || !selectedWorkout) {
      setProgram(null);
      return undefined;
    }
    let cancelled = false;
    resetAutosave();
    setLoading(true);
    setLoadError(null);
    api
      .post("WorkoutAdmin/GetProgramAndMovementMatrix", {
        OrgId: orgId,
        PhaseId: selectedPhase.value,
        PeriodId: selectedPeriod.value,
        OrgWorkoutId: selectedWorkout.value,
        ActiveOnly: true,
      })
      .then((r) => {
        if (cancelled) return;
        if (r.data && r.data.success) {
          // Builder-only screen — the returned movementMatrixList (difficulty matrix) is
          // intentionally ignored, per the plan.
          setProgram(r.data.message.programBuilderVM);
        } else {
          setLoadError((r.data && r.data.message) || "Could not load this workout.");
          setProgram(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        api.catchHandler(err);
        setLoadError("Could not load this workout.");
        setProgram(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, selectedPhase, selectedPeriod, selectedWorkout]);

  // Report this column's identity + movement ids up to the page for cross-column duplicate/
  // conflict detection. Fires even before a program is loaded so the "same workout picked in two
  // columns" conflict warning can appear immediately.
  useEffect(() => {
    const movementIds = program
      ? _.uniq(
          _.flatMap(program.categories, (c) => _.map(c.movements, (m) => m.movementId))
        ).filter((id) => !!id)
      : [];
    onReport(columnId, {
      phaseId: selectedPhase ? selectedPhase.value : null,
      periodId: selectedPeriod ? selectedPeriod.value : null,
      workoutId: selectedWorkout ? selectedWorkout.value : null,
      movementIds,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnId, selectedPhase, selectedPeriod, selectedWorkout, program]);

  // Stop contributing to page-level duplicate/conflict counts once this column is removed.
  useEffect(() => {
    return () => onReport(columnId, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnId]);

  // Ported (verbatim where possible) from ProgramMovementMatrix.jsx's immutable edit callbacks,
  // scoped to this column's own `program` state. All are useCallback([]) and only ever touch
  // `program` via the functional setState form, so they're stable across this column's entire
  // lifetime and never read anything belonging to another column. NONE of this data-flow changed
  // for the mockup redesign — only how WorkoutSection invokes these callbacks did.
  const onCategoryMovementChange = useCallback((categorySequence, movementSequence, movementSelect) => {
    setProgram((prev) => {
      if (!prev) return prev;
      const cIdx = categorySequence - 1;
      const mIdx = movementSequence - 1;
      const categories = prev.categories.map((c, i) => {
        if (i !== cIdx) return c;
        const movements = c.movements.map((mv, j) =>
          j === mIdx
            ? { ...mv, movementId: movementSelect.value, name: movementSelect.label }
            : mv
        );
        return { ...c, movements };
      });
      return { ...prev, categories };
    });
  }, []);

  const onCategoryHeaderChange = useCallback((categorySequence, typeOfHeader, value) => {
    const nextValue = !value || !value.length ? null : value;
    setProgram((prev) => {
      if (!prev) return prev;
      const cIdx = categorySequence - 1;
      const categories = prev.categories.map((c, i) =>
        i === cIdx
          ? { ...c, category: { ...c.category, [typeOfHeader]: nextValue } }
          : c
      );
      return { ...prev, categories };
    });
  }, []);

  // Inserts one new, blank section DIRECTLY BELOW the section at `categorySequence` (1-based), then
  // re-sequences. Called with 0 from the empty-workout bootstrap so it seeds the first block at the
  // top. Starts blank — it does not clone (contrast ProgramMovementMatrix.jsx, which clones).
  const addSection = useCallback((categorySequence) => {
    setProgram((prev) => {
      if (!prev) return prev;
      const insertIndex = categorySequence; // directly after the source (1-based seq -> array index)
      const categories = [...prev.categories];
      categories.splice(insertIndex, 0, {
        category: { id: 0, seq: 0, header: "", setsSubheader: "", repsSubheader: "" },
        movements: [{ seq: 1, movementId: 0, name: "" }],
      });
      return {
        ...prev,
        categories: categories.map((c, i) => ({ ...c, category: { ...c.category, seq: i + 1 } })),
      };
    });
  }, []);

  const removeCategory = useCallback((categorySequence) => {
    setProgram((prev) => {
      if (!prev) return prev;
      const categories = prev.categories
        .filter((_c, i) => i !== categorySequence - 1)
        .map((c, i) => ({ ...c, category: { ...c.category, seq: i + 1 } }));
      return { ...prev, categories };
    });
  }, []);

  // Inserts a single blank movement row DIRECTLY BELOW `movementSequence` (1-based) in the target
  // category, then re-sequences. Called with 0 from the empty-section bootstrap so it seeds the
  // first row at the top. Blank — not a clone of the clicked row. No-ops if the category is missing.
  const addMovementToCategory = useCallback((categorySequence, movementSequence) => {
    setProgram((prev) => {
      if (!prev) return prev;
      const cIdx = categorySequence - 1;
      if (!prev.categories[cIdx]) return prev;
      const categories = prev.categories.map((c, i) => {
        if (i !== cIdx) return c;
        const movements = [...c.movements];
        movements.splice(movementSequence, 0, { seq: 0, movementId: 0, name: "" }); // below clicked
        return { ...c, movements: movements.map((mv, j) => ({ ...mv, seq: j + 1 })) };
      });
      return { ...prev, categories };
    });
  }, []);

  const removeMovementfromCategory = useCallback((categorySequence, movementSequence) => {
    setProgram((prev) => {
      if (!prev) return prev;
      const cIdx = categorySequence - 1;
      const mIdx = movementSequence - 1;
      const categories = prev.categories.map((c, i) => {
        if (i !== cIdx) return c;
        const movements = c.movements
          .filter((_mv, j) => j !== mIdx)
          .map((mv, j) => ({ ...mv, seq: j + 1 }));
        return { ...c, movements };
      });
      return { ...prev, categories };
    });
  }, []);

  const workoutSelected = !!(selectedPhase && selectedPeriod && selectedWorkout);
  const categories = program ? program.categories : [];
  // Only surface a chip while actively saving, briefly after a save finishes, or on error — the
  // hook clears "saved" back to idle after a short delay, so a settled column shows no chip.
  const chip = STATUS_CHIP[saveStatus];

  return (
    <div className="workout-builder-column shrink-0">
      <div className="workout-builder-column-head">
        <div className="workout-builder-selects">
          <FilterSelect
            filterName="PhaseId"
            displayName="Phase"
            value={selectedPhase}
            options={phaseList || []}
            styles={COMPACT_SELECT_STYLES}
            menuPortalTarget={MENU_PORTAL_TARGET}
            onChangeCallback={(e) => {
              setSelectedPhase(e.value);
              setSelectedPeriod(null);
              setSelectedWorkout(null);
              if (activeWorkoutsOnly) {
                setActiveWorkoutList([]);
                loadFilteredPeriods(e.value ? e.value.value : null);
              }
            }}
          />
          <FilterSelect
            filterName="PeriodId"
            displayName="Period"
            value={selectedPeriod}
            options={effectivePeriodList}
            styles={COMPACT_SELECT_STYLES}
            menuPortalTarget={MENU_PORTAL_TARGET}
            onChangeCallback={(e) => {
              setSelectedPeriod(e.value);
              setSelectedWorkout(null);
              if (activeWorkoutsOnly) {
                const phaseId = selectedPhase ? selectedPhase.value : null;
                loadFilteredWorkouts(phaseId, e.value ? e.value.value : null);
              }
            }}
          />
          <FilterSelect
            filterName="OrgWorkoutId"
            displayName="Workout"
            value={selectedWorkout}
            options={effectiveWorkoutList}
            styles={COMPACT_SELECT_STYLES}
            menuPortalTarget={MENU_PORTAL_TARGET}
            onChangeCallback={(e) => setSelectedWorkout(e.value)}
          />
        </div>
        {canRemove ? (
          <button
            type="button"
            className="workout-builder-icon-btn workout-builder-remove-column"
            title="Remove this column"
            aria-label="Remove this column"
            onClick={() => {
              if (window.confirm("Remove this workout column?")) onRemove(columnId);
            }}
          >
            <MdClose aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className="workout-builder-column-status">
        {chip ? (
          <span className={`workout-builder-status-chip ${chip.className}`}>{chip.label}</span>
        ) : null}
        {hasSelectionConflict ? (
          <span
            className="workout-builder-conflict-badge"
            title="Another visible column has the same Phase/Period/Workout selected — autosaving both can conflict."
          >
            <FontAwesomeIcon icon="exclamation-triangle" /> Same workout in another column
          </span>
        ) : null}
      </div>

      {loadError ? (
        <Alert2 className="danger" style={{ fontSize: "0.8rem" }}>
          {loadError}
        </Alert2>
      ) : null}

      {!workoutSelected ? (
        <div className="workout-builder-placeholder">
          Select a Phase, Period, and Workout to begin.
        </div>
      ) : loading && !program ? (
        <div className="workout-builder-placeholder">Loading…</div>
      ) : program ? (
        <div className={loading ? "opacity-50 pointer-events-none" : ""}>
          {_.map(categories, (pc) => (
            <WorkoutSection
              key={`workout-cat-${pc.category.seq}`}
              category={pc}
              mvmtList={mvmtList}
              duplicatedMovements={duplicatedMovements}
              highlightDuplicates={highlightDuplicates}
              changeCategoryMovementCallback={onCategoryMovementChange}
              changeHeaderCallback={onCategoryHeaderChange}
              addSectionCallback={addSection}
              removeCategoryCallback={removeCategory}
              addMovementCallback={addMovementToCategory}
              removeMovementCallback={removeMovementfromCategory}
            />
          ))}
          {/* Per-header add(+) buttons are the primary way to add sections. A genuinely empty
              workout has no header to hang a (+) on, so surface a single bootstrap link that seeds
              the first blank block (seq 0 -> inserted at the top). */}
          {categories.length === 0 ? (
            <button
              type="button"
              className="workout-builder-add-link workout-builder-add-section"
              onClick={() => addSection(0)}
            >
              + Add section
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
